import type { Fnode } from "@protontech/fathom";

import { MIN_AREA_SUBMIT_BTN } from "@protontech/autofill/constants/heuristics";
import { inputCandidateSelector } from "@protontech/autofill/constants/selectors";
import type { FieldType } from "@protontech/autofill/types";
import { any } from "./combinators";
import { getAllFieldHaystacks } from "./extract";
import { getParentFormFnode } from "./fathom";
import { isClassifiable, isHidden, isProcessed } from "./flags";
import { matchEmail, matchOAuth, matchUsername } from "./re";
import { isVisible, isVisibleField } from "./visible";

export type HTMLFieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export const isInput = (el: HTMLElement): el is HTMLInputElement => el.tagName === "INPUT";
export const isInputField = (el: HTMLElement): el is HTMLInputElement => {
  if (!isInput(el)) return false;
  return el.type !== "button" && el.type !== "submit";
};

export const isSelect = (el: HTMLElement): el is HTMLSelectElement => el.tagName === "SELECT";
export const isTextarea = (el: HTMLElement): el is HTMLTextAreaElement => el.tagName === "TEXTAREA";

/* Split a set of elements by their visibility - this is useful
 * when building the abstract form feature vector when computing
 * ratios between hidden & visible fields */
export const splitFieldsByVisibility = (els: HTMLElement[]) => {
  return els.reduce<[HTMLElement[], HTMLElement[]]>(
    (acc, el) => {
      if (isVisibleField(el)) acc[0].push(el);
      else acc[1].push(el);

      return acc;
    },
    [[], []],
  );
};

export const fType = (type: FieldType) => (fnode: Fnode) => fnode.hasType(type);

export const fInput = (types: string[]) => (fnode: Fnode) => types.includes((fnode.element as HTMLFieldElement).type);
export const fMatch = (selector: string) => (fnode: Fnode) => fnode.element.matches(selector);
export const fMode = (mode: string) => (fnode: Fnode) => fnode.element.inputMode === mode;
export const fList = (fnode: Fnode) => fnode.element.getAttribute("aria-autocomplete") === "list" || fnode.element.role === "combobox";

export const isUsernameCandidate = (el: HTMLInputElement) => el.type === "text" || (el.type === "tel" && any(matchUsername)(getAllFieldHaystacks(el)));
export const isEmailCandidate = (el: HTMLInputElement) => el.type === "email" || (el.type === "text" && any(matchEmail)(getAllFieldHaystacks(el)));
export const isOAuthCandidate = (el: HTMLElement) => any(matchOAuth)(getAllFieldHaystacks(el));
export const isMFACandidate = (el: HTMLFieldElement) => ["text", "number", "tel"].includes(el.type);

/* exclude buttons which do not have some inner text : this
 * should get rid of most icon based buttons */
export const isBtnCandidate = (btn: HTMLElement) => {
  if (btn.getAttribute("type") === "submit") return true;
  if (btn.innerText.trim().length <= 1) return false;

  const height = btn.offsetHeight;
  const width = btn.offsetWidth;
  return height * width > MIN_AREA_SUBMIT_BTN;
};

/* exposed for consumer of protonpass-fathom in order to
 * to apply the same heuristics for selecting editable fields
 * as the detectors. Useful for checking if we should run the
 * detectors when new fields appear */
export const isProcessableField = (input: HTMLElement) => {
  const processed = isProcessed(input);
  const hidden = isHidden(input);
  return (!processed || hidden) && isVisibleField(input) && isVisible(input, { opacity: false });
};
export const isClassifiableField = (fnode: Fnode) => fnode.element.tagName === "INPUT" && isClassifiable(fnode.element) && getParentFormFnode(fnode) !== null;

export const selectInputCandidates = (target: Document | HTMLElement = document) => Array.from(target.querySelectorAll<HTMLInputElement>(inputCandidateSelector)).filter(isClassifiable);
